import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Menu,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { groupAPI } from "../../services/api";
import { isAuthTransitionError } from "../../services/api/api";
import { GroupInvitation } from "../../lib/types";

type ConfirmState = null | {
  action: "accept" | "decline";
  invite: GroupInvitation;
};

const formatGroupLabel = (invite: GroupInvitation) => {
  const groupId = String(invite.groupId || "");
  const suffix = groupId ? ` (#${groupId.slice(-6)})` : "";
  const groupName = String(invite.groupName || "").trim();
  if (groupName) {
    return `${groupName}${suffix}`;
  }
  return groupId ? `${groupId}${suffix}` : "Unknown group";
};

const InvitationsBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invitations, setInvitations] = useState<GroupInvitation[]>([]);

  const [confirmState, setConfirmState] = useState<ConfirmState>(null);
  const [decisionLoading, setDecisionLoading] = useState<string | null>(null);

  const pendingCount = invitations.length;

  const loadInvitations = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      console.log("[InvitationsBell] Loading invitations");
      const invites = await groupAPI.invitations.listUserInvites();
      console.log("[InvitationsBell] Loaded invitations", {
        count: invites.length,
      });
      setInvitations(invites);
    } catch (e) {
      if (!isAuthTransitionError(e)) {
        console.error("[InvitationsBell] Failed to load invitations", e);
        setError("Failed to load invitations");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Load once on mount so badge shows without needing to click.
    loadInvitations();
  }, [loadInvitations]);

  useEffect(() => {
    if (!open) return;
    // Refresh when opening to avoid stale list.
    loadInvitations();
  }, [open, loadInvitations]);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const sortedInvitations = useMemo(() => {
    return [...invitations].sort((a, b) => {
      const aDate = a.invitedAt ? Date.parse(a.invitedAt) : 0;
      const bDate = b.invitedAt ? Date.parse(b.invitedAt) : 0;
      return bDate - aDate;
    });
  }, [invitations]);

  const handleConfirm = async () => {
    if (!confirmState) return;
    const invite = confirmState.invite;
    const action = confirmState.action;
    const inviteId = invite.id;

    try {
      setDecisionLoading(inviteId);
      console.log("[InvitationsBell] Invitation decision", {
        action,
        inviteId,
        groupId: invite.groupId,
      });

      if (action === "accept") {
        await groupAPI.invitations.accept(inviteId);
        window.dispatchEvent(
          new CustomEvent("groups:membership-updated", {
            detail: { action: "accept", groupId: invite.groupId, inviteId },
          })
        );
      } else {
        await groupAPI.invitations.decline(inviteId);
      }

      setInvitations((prev) => prev.filter((i) => i.id !== inviteId));
      setConfirmState(null);
    } catch (e) {
      if (!isAuthTransitionError(e)) {
        console.error("[InvitationsBell] Failed invitation decision", e);
        setError(`Failed to ${action} invitation`);
      }
      setConfirmState(null);
    } finally {
      setDecisionLoading(null);
    }
  };

  return (
    <>
      <Tooltip title="Invitations">
        <IconButton
          onClick={handleOpen}
          size="small"
          sx={{ position: "relative" }}
        >
          <Badge
            badgeContent={pendingCount}
            color="error"
            overlap="circular"
            invisible={pendingCount === 0}
          >
            <NotificationsNoneIcon fontSize="small" />
          </Badge>
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        PaperProps={{ sx: { width: 420, maxWidth: "90vw" } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Invitations
            </Typography>
            <Button
              size="small"
              onClick={loadInvitations}
              disabled={loading}
              sx={{ textTransform: "none" }}
            >
              Refresh
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Pending: {pendingCount}
          </Typography>
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{ px: 2, py: 2, display: "flex", justifyContent: "center" }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography color="error.main" variant="body2">
              {error}
            </Typography>
          </Box>
        ) : sortedInvitations.length === 0 ? (
          <Box sx={{ px: 2, py: 2 }}>
            <Typography variant="body2" color="text.secondary">
              No pending invitations.
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding>
            {sortedInvitations.map((invite) => {
              const busy = decisionLoading === invite.id;
              return (
                <ListItem
                  key={invite.id}
                  sx={{
                    px: 2,
                    py: 1.25,
                    gap: 1,
                    alignItems: "flex-start",
                  }}
                  secondaryAction={
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <Tooltip title="Accept">
                        <span>
                          <IconButton
                            size="small"
                            color="success"
                            disabled={busy}
                            onClick={() =>
                              setConfirmState({ action: "accept", invite })
                            }
                          >
                            <CheckCircleIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title="Decline">
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={busy}
                            onClick={() =>
                              setConfirmState({ action: "decline", invite })
                            }
                          >
                            <CancelIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {busy && <CircularProgress size={16} />}
                    </Stack>
                  }
                >
                  <ListItemText
                    primary={
                      <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                        {formatGroupLabel(invite)}
                      </Typography>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.25 }}
                      >
                        Role: {invite.role}
                      </Typography>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Menu>

      <Dialog
        open={Boolean(confirmState)}
        onClose={() => setConfirmState(null)}
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          {confirmState?.action === "accept"
            ? "Accept invitation?"
            : "Decline invitation?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            Group: {confirmState ? formatGroupLabel(confirmState.invite) : "-"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Role: {confirmState?.invite.role ?? "-"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmState(null)}>Cancel</Button>
          <Button
            onClick={handleConfirm}
            color={confirmState?.action === "accept" ? "success" : "error"}
            variant="contained"
          >
            {confirmState?.action === "accept" ? "Accept" : "Decline"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default InvitationsBell;
